import { useCallback, useMemo } from 'react';

import { getCategories } from 'hb-react/backoffice/components/CategoriesList/requests';
import { type CategoriesResponse } from 'hb-react/backoffice/components/CategoriesList/types';
import { makeConfigUrls } from 'hb-react/backoffice/components/CategoriesList/utils/configUrls';
import { PER_PAGE } from 'hb-react/shared/components/ReactSelect/constants';
import ReactSelectAdaptive from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive';
import { type QueryFn } from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive/types';
import { type ReactSelectCategoriesProps } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/types';
import { categoriesToOptions } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/utils/categoriesToOptions';

const extractData = (response: CategoriesResponse) => response.data;
const shouldUseAsync = (defaultOptionsQueryResponse: CategoriesResponse) => {
  if (defaultOptionsQueryResponse.total === null) {
    return false;
  }

  return (
    defaultOptionsQueryResponse.total > defaultOptionsQueryResponse.data.length
  );
};

/**
 * NOTE: this component uses a backoffice endpoint to fetch categories
 */
export const ReactSelectCategories = ({
  categorizedType,
  isMulti,
  label = __('Filter by category'),
  networkId,
  onChange,
  placeholder = __('Search and select a category'),
  topicId,
  availableForNetwork,
  value,
  ...otherReactSelectProps
}: ReactSelectCategoriesProps) => {
  const queryFn = useCallback<QueryFn<CategoriesResponse>>(
    ({ searchText, ids }, signal) => {
      const configUrls = makeConfigUrls({
        networkId,
        type: categorizedType,
        topicId,
        availableForNetwork,
        page: 1,
        hasPagination: true,
        perPage: ids ? ids.length : PER_PAGE,
        searchKeyword: searchText,
        ids: ids,
      });

      return getCategories(configUrls, signal);
    },
    [networkId, categorizedType, topicId, availableForNetwork],
  );

  const queryKey = useMemo(
    () => [networkId, topicId, 'categories', categorizedType],
    [networkId, topicId, categorizedType],
  );

  return (
    <ReactSelectAdaptive
      queryKey={queryKey}
      queryFn={queryFn}
      extractData={extractData}
      shouldUseAsync={shouldUseAsync}
      dataToOptions={categoriesToOptions}
      isMulti={isMulti}
      label={label}
      placeholder={placeholder}
      onChange={onChange}
      value={value}
      {...otherReactSelectProps}
    />
  );
};

export default ReactSelectCategories;
